import { Link } from 'react-router-dom';
import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useRegionalPageContent } from '@/hooks/useRegionalPageContent';
import { Hero } from '@/components/sections/Hero';
import { ThreeDoor } from '@/components/sections/ThreeDoor';
import { DiagnosticCTA } from '@/components/diagnostic/DiagnosticCTA';
import { TrustLogos } from '@/components/sections/TrustLogos';
import { Framework } from '@/components/sections/Framework';
import { CaseStudies } from '@/components/sections/CaseStudies';
import { VideoTestimonials } from '@/components/sections/VideoTestimonials';
import { ImageBanner } from '@/components/sections/ImageBanner';
import { Proof } from '@/components/sections/Proof';
import { FAQ } from '@/components/sections/FAQ';
import { CommitmentLadder } from '@/components/shared/CommitmentLadder';
import { RegionGate } from '@/components/ui/RegionGate';
import { ArrowRight, Star } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { useTranslation } from 'react-i18next';
import { ScrollReveal } from '@/components/ui/ScrollReveal';

export function HomePage() {
  const { t } = useTranslation();
  const { content: regionalContent } = useRegionalPageContent('home');

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "What makes the FIVE LENS framework different from standard Enneagram assessments?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "The FIVE LENS framework integrates the Enneagram with four additional lenses of psychological insight: Personal Mastery and Maturity, Emotional Resilience, Social Drives, and Energy Centers. This multidimensional approach provides data-driven depth beyond single-lens personality typing, measuring resilience, social drives, and vertical leadership maturity for coaching, culture change, and leadership development."
        }
      },
      {
        "@type": "Question",
        "name": "Is the assessment scientifically validated?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Yes. The Five Lens Development Platform has been the subject of an independent PhD study at Walden University, which validated its application as a framework for integrating emotional intelligence principles, skills, and abilities in organisational settings."
        }
      },
      {
        "@type": "Question",
        "name": "Can this framework be used for entire teams, or is it just for individuals?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "The FIVE LENS framework is designed for both individuals and teams. Individual assessments provide personal coaching insights, while team reports and facilitated workshops help organisations resolve systemic friction, align executive teams, and build high-trust performance cultures."
        }
      },
      {
        "@type": "Question",
        "name": "How long does the certification process take for practitioners?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "The Practitioner Certification (Level 1) includes 8-10 hours of self-paced online learning and 6 live Zoom sessions over 2-3 weeks, plus guided case studies. It is ICF-accredited at 23.75 CCE points. The Team Facilitator Certification (Level 2) includes 8-10 hours pre-course online and 4 live sessions over 4 weeks."
        }
      }
    ]
  };

  return (
    <main>
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify(faqSchema)}
        </script>
      </Helmet>
      <Hero />
      
      {regionalContent && (regionalContent.title || regionalContent.htmlContent) && (
        <section className="bg-ground py-16 border-b border-ground-deep">
          <div className="max-w-4xl mx-auto px-6 text-center">
            {regionalContent.title && (
              <h2 className="text-3xl font-serif text-ink mb-6">{regionalContent.title}</h2>
            )}
            {regionalContent.htmlContent && (
              <div 
                className="prose prose-brand max-w-none text-ink/70"
                dangerouslySetInnerHTML={{ __html: regionalContent.htmlContent }}
              />
            )}
          </div>
        </section>
      )}
      <ScrollReveal>
        <TrustLogos />
      </ScrollReveal>
      <ScrollReveal>
        <ThreeDoor />
      </ScrollReveal>
      
      <ScrollReveal>
        <RegionGate allowedRegions={['us']}>
          <div className="bg-ink py-8 text-center text-white border-b border-ground-deep">
            <div className="max-w-7xl mx-auto px-6 flex flex-col sm:flex-row items-center justify-center gap-4"> 
              <Star className="text-rule-3 w-6 h-6" /> 
              <p className="text-lg font-serif"><strong>{t('home.usExclusive', 'Exclusive for US organisations:')}</strong> {t('home.usOffer', 'Get 20% off our Enterprise Licensing when you sign up this month.')}</p> 
              <Link to="/contact"><Button variant="primary" className="bg-rule-3 text-white hover:bg-white hover:text-ink text-sm py-2 h-auto gap-2">{t('home.claimOffer', 'Claim Offer')} <ArrowRight size={16} /></Button></Link>
            </div>
          </div>
        </RegionGate>
      </ScrollReveal>
      
      <ScrollReveal>
        <DiagnosticCTA />
      </ScrollReveal>
      
      <ScrollReveal>
        <Proof />
      </ScrollReveal>

      <ScrollReveal>
        <Framework />
      </ScrollReveal>
      
      <ScrollReveal>
        <CaseStudies />
      </ScrollReveal>
      
      <VideoTestimonials />
      
      <ScrollReveal>
        <ImageBanner />
      </ScrollReveal>
      
      <ScrollReveal>
        <FAQ />
      </ScrollReveal>
    </main>
  );
}
